import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateSchedule } from "@/features/calendar/hooks/use-schedules";
import type { Schedule } from "@/features/calendar/types";
import { useEffect, useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import { ScheduleForm } from "./schedule-form";

interface EditScheduleDialogProps {
  schedule: Schedule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditScheduleDialog = ({
  schedule,
  open,
  onOpenChange,
}: EditScheduleDialogProps) => {
  const { t } = useTranslation("schedules");
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [rrules, setRrules] = useState("");

  const updateSchedule = useUpdateSchedule();

  useEffect(() => {
    if (schedule) {
      setName(schedule.name);
      setTimezone(schedule.timezone);
      setRrules(schedule.rrules);
    }
  }, [schedule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schedule) return;

    try {
      await updateSchedule.mutateAsync({
        scheduleId: schedule.id,
        data: {
          name,
          timezone,
          rrules,
        },
      });

      toast.success(t("messages.updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.updateError"),
      );
    }
  };

  if (!schedule) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("editDialog.title")}</DialogTitle>
          <DialogDescription>{t("editDialog.description")}</DialogDescription>
        </DialogHeader>

        <ScheduleForm
          name={name}
          timezone={timezone}
          rrules={rrules}
          onNameChange={setName}
          onTimezoneChange={setTimezone}
          onRrulesChange={setRrules}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          submitLabel={t("form.save")}
          isSubmitting={updateSchedule.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
